import React, { useMemo, useState, useCallback } from "react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Plus, Trash2 } from "lucide-react";
import {
  Heart,
  Car,
  Stethoscope,
  GraduationCap,
  Home,
  Key,
} from "lucide-react";
import { useGesellschaften } from "@/hooks/useGesellschaften";
import { useKunden } from "@/hooks/useKunden";
import { useLeads } from "@/hooks/useLeads";
import { SearchableSelect, SearchableSelectItem } from "@/components/ui/searchable-select";

interface ChildEntry {
  id: string;
  vorname: string;
  nachname: string;
  geburtsdatum: string;
  linkedEntityId?: string;
  linkedEntityType?: "kunde" | "lead";
}

interface ProvisionProduct {
  id: string;
  produkt: string;
  gesellschaftId: string;
  kindId: string; // Single child ID instead of array
}

interface ChildPlanningFormProps {
  formValues: Record<string, any>;
  onFieldChange: (fieldId: string, value: any) => void;
  className?: string;
}

const SUPPORT_AREAS = [
  { id: "hochzeit", label: "Hochzeit", icon: Heart },
  { id: "fuehrerschein", label: "Führerschein", icon: Key },
  { id: "gesundheit", label: "Gesundheits-vorsorge", icon: Stethoscope },
  { id: "autokauf", label: "Autokauf", icon: Car },
  { id: "ausbildung", label: "Ausbildung", icon: GraduationCap },
  { id: "immobilie", label: "Wohnungs-/\nHauskauf", icon: Home },
];

const DEFAULT_PRODUCTS = [
  "Lebensversicherung",
  "Bausparer",
  "Unfallvorsorge",
  "Gesundheitsvorsorge",
  "Wertpapiere",
  "Edelmetalle",
  "Sonstige",
];

const createEmptyChild = (): ChildEntry => ({
  id: crypto.randomUUID(),
  vorname: "",
  nachname: "",
  geburtsdatum: "",
});

const createEmptyProduct = (): ProvisionProduct => ({
  id: crypto.randomUUID(),
  produkt: "",
  gesellschaftId: "",
  kindId: "",
});

export function ChildPlanningForm({
  formValues,
  onFieldChange,
  className,
}: ChildPlanningFormProps) {
  const children: ChildEntry[] = formValues.kinder || [];
  const supportAreas: Record<string, boolean> = formValues.unterstuetzungsbereiche || {};
  
  // Defensive: Handle old object format or missing data
  const rawProvisions = formValues.vorsorgeprodukte;
  const provisions: ProvisionProduct[] = Array.isArray(rawProvisions) 
    ? rawProvisions.map(p => ({
        ...p,
        // Migrate old kinderIds array to single kindId
        kindId: p.kindId || (Array.isArray(p.kinderIds) && p.kinderIds.length > 0 ? p.kinderIds[0] : "")
      }))
    : DEFAULT_PRODUCTS.map(p => ({ id: crypto.randomUUID(), produkt: p, gesellschaftId: "", kindId: "" }));

  const { data: gesellschaften = [] } = useGesellschaften({ select: 'basic' });
  
  // Search term for Kind dropdown - only search customers/leads when user types
  const [kindSearchTerm, setKindSearchTerm] = useState("");
  const hasKindSearch = kindSearchTerm.trim().length >= 2;
  
  const { data: kunden = [] } = useKunden({ 
    select: 'basic', 
    search: kindSearchTerm.trim(),
    enabled: hasKindSearch,
    limit: 20
  });
  const { data: leads = [] } = useLeads({ 
    enabled: hasKindSearch 
  });

  const gesellschaftOptions = useMemo(() => 
    gesellschaften.map(g => ({ value: g.id, label: g.name })),
    [gesellschaften]
  );

  const produktOptions: SearchableSelectItem[] = useMemo(() => 
    DEFAULT_PRODUCTS.map(p => ({ value: p, label: p })),
    []
  );

  // Child options: manually added children always shown, customers/leads only when searching
  const kindOptions: SearchableSelectItem[] = useMemo(() => {
    const items: SearchableSelectItem[] = [];
    
    // Add manually added children first (always shown)
    if (children.length > 0) {
      children.forEach((child, idx) => {
        const name = `${child.vorname || ''} ${child.nachname || ''}`.trim();
        items.push({
          value: child.id,
          label: name || `Kind ${idx + 1}`,
          group: "Kinder"
        });
      });
    }
    
    // Add customers only if searching (and results exist)
    if (hasKindSearch) {
      // Filter leads by search term client-side
      const searchLower = kindSearchTerm.toLowerCase();
      
      kunden.forEach(k => {
        items.push({
          value: `kunde:${k.id}`,
          label: `${k.vorname || ''} ${k.zuname || ''}`.trim() || k.email || 'Unbenannter Kunde',
          group: "Kunden"
        });
      });
      
      // Filter leads client-side by search term
      leads
        .filter(l => {
          const name = (l.name || '').toLowerCase();
          const email = (l.email || '').toLowerCase();
          return name.includes(searchLower) || email.includes(searchLower);
        })
        .slice(0, 20)
        .forEach(l => {
          items.push({
            value: `lead:${l.id}`,
            label: l.name || l.email || 'Unbenannter Lead',
            group: "Leads"
          });
        });
    }
    
    return items;
  }, [children, kunden, leads, hasKindSearch, kindSearchTerm]);
  
  // Handler for search input change in Kind dropdown
  const handleKindSearchChange = useCallback((search: string) => {
    setKindSearchTerm(search);
  }, []);

  const updateChildren = (newChildren: ChildEntry[]) => {
    onFieldChange("kinder", newChildren);
  };

  const addChild = () => {
    updateChildren([...children, createEmptyChild()]);
  };

  const removeChild = (index: number) => {
    const updated = [...children];
    const removedId = updated[index].id;
    updated.splice(index, 1);
    updateChildren(updated);
    
    // Clear from provisions
    const updatedProvisions = provisions.map(p => ({
      ...p,
      kindId: p.kindId === removedId ? "" : p.kindId
    }));
    onFieldChange("vorsorgeprodukte", updatedProvisions);
  };

  const updateChild = (index: number, field: keyof ChildEntry, value: string) => {
    const updated = [...children];
    updated[index] = { ...updated[index], [field]: value };
    updateChildren(updated);
  };

  const toggleSupportArea = (areaId: string) => {
    const updated = { ...supportAreas, [areaId]: !supportAreas[areaId] };
    onFieldChange("unterstuetzungsbereiche", updated);
  };

  const updateProvision = (index: number, field: keyof ProvisionProduct, value: any) => {
    const updated = [...provisions];
    updated[index] = { ...updated[index], [field]: value };
    onFieldChange("vorsorgeprodukte", updated);
  };

  const addProduct = () => {
    onFieldChange("vorsorgeprodukte", [...provisions, createEmptyProduct()]);
  };

  const removeProduct = (index: number) => {
    const updated = [...provisions];
    updated.splice(index, 1);
    onFieldChange("vorsorgeprodukte", updated);
  };

  const getKindDisplayLabel = (kindId: string) => {
    if (!kindId) return "Keine";
    
    // Check in manually added children first
    const childIdx = children.findIndex(c => c.id === kindId);
    if (childIdx >= 0) {
      const child = children[childIdx];
      const name = `${child.vorname || ''} ${child.nachname || ''}`.trim();
      return name || `Kind ${childIdx + 1}`;
    }
    
    // Check if it's a customer/lead reference
    if (kindId.startsWith('kunde:')) {
      const kundeId = kindId.replace('kunde:', '');
      const kunde = kunden.find(k => k.id === kundeId);
      if (kunde) {
        return `${kunde.vorname || ''} ${kunde.zuname || ''}`.trim() || 'Kunde';
      }
    }
    
    if (kindId.startsWith('lead:')) {
      const leadId = kindId.replace('lead:', '');
      const lead = leads.find(l => l.id === leadId);
      if (lead) {
        return lead.name || 'Lead';
      }
    }
    
    return "Keine";
  };

  return (
    <div className={cn("p-6 space-y-8", className)}>
      {/* Title */}
      <div className="text-left">
        <h2 className="text-2xl md:text-3xl font-bold">
          <span className="text-foreground">Kinder & </span>
          <span className="text-primary">Zukunftsplanung</span>
        </h2>
        <p className="text-lg italic text-muted-foreground mt-1">
          Heute an morgen denken.
        </p>
      </div>

      {/* Section: Kinderdaten */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-foreground">
            Was wünschen Sie sich für Ihre Kinder?
          </h3>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addChild}
            className="gap-1"
          >
            <Plus className="h-4 w-4" />
            Kind hinzufügen
          </Button>
        </div>

        {children.length === 0 ? (
          <div className="text-center py-6 bg-muted/30 rounded-lg border-2 border-dashed">
            <p className="text-sm text-muted-foreground mb-2">
              Noch keine Kinder erfasst
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addChild}
            >
              Erstes Kind hinzufügen
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {children.map((child, index) => (
              <div
                key={child.id}
                className="grid grid-cols-[1fr_1fr_1fr_auto] gap-3 items-end p-3 bg-muted/30 rounded-lg border"
              >
                <div className="space-y-1.5">
                  <Label className="text-xs">Vorname (Kind {index + 1})</Label>
                  <Input
                    value={child.vorname}
                    onChange={(e) => updateChild(index, "vorname", e.target.value)}
                    placeholder="Vorname"
                    className="h-9"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Nachname</Label>
                  <Input
                    value={child.nachname}
                    onChange={(e) => updateChild(index, "nachname", e.target.value)}
                    placeholder="Nachname"
                    className="h-9"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Geburtsdatum</Label>
                  <Input
                    type="date"
                    value={child.geburtsdatum}
                    onChange={(e) => updateChild(index, "geburtsdatum", e.target.value)}
                    className="h-9"
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeChild(index)}
                  className="h-9 w-9 text-muted-foreground hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Section: Unterstützungsbereiche */}
      <div className="space-y-4">
        <h3 className="font-semibold text-foreground">
          In welchen Bereichen möchten Sie Ihre Kinder unterstützen?
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {SUPPORT_AREAS.map((area) => {
            const Icon = area.icon;
            const isSelected = supportAreas[area.id];
            return (
              <button
                key={area.id}
                type="button"
                onClick={() => toggleSupportArea(area.id)}
                className={cn(
                  "relative flex flex-col items-start p-4 rounded-lg border-2 transition-all text-left min-h-[90px]",
                  isSelected
                    ? "bg-primary/10 border-primary"
                    : "bg-muted/30 border-transparent hover:border-primary/50"
                )}
              >
                <span className="text-xs sm:text-sm font-medium text-primary leading-snug pr-7 w-full whitespace-pre-line">
                  {area.label}
                </span>
                <Icon className={cn(
                  "h-7 w-7 mt-auto",
                  isSelected ? "text-primary" : "text-muted-foreground"
                )} />
                <div className="absolute top-3 right-3">
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => toggleSupportArea(area.id)}
                    className="pointer-events-none"
                  />
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Section: Vorsorgeprodukte Tabelle */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-foreground">
            Wie haben Sie bisher für Ihre Kinder vorgesorgt?
          </h3>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addProduct}
            className="gap-1"
          >
            <Plus className="h-4 w-4" />
            Produkt hinzufügen
          </Button>
        </div>
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50">
                <th className="text-left p-3 font-semibold">Produkt</th>
                <th className="text-left p-3 font-semibold">Gesellschaft</th>
                <th className="text-left p-3 font-semibold text-primary whitespace-nowrap">
                  Kind
                  {children.length > 0 && (
                    <span className="ml-1 text-xs font-normal text-muted-foreground">
                      ({children.length})
                    </span>
                  )}
                </th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {provisions.map((provision, index) => (
                <tr key={provision.id} className="border-t">
                  <td className="p-3">
                    <SearchableSelect
                      items={produktOptions}
                      value={provision.produkt}
                      onValueChange={(val) => updateProvision(index, "produkt", val)}
                      placeholder="Produkt"
                      emptyText="Kein Produkt gefunden"
                      className="h-8"
                      allowClear
                    />
                  </td>
                  <td className="p-3">
                    <SearchableSelect
                      items={gesellschaftOptions}
                      value={provision.gesellschaftId}
                      onValueChange={(val) => updateProvision(index, "gesellschaftId", val)}
                      placeholder="Anbieter"
                      emptyText="Kein Anbieter gefunden"
                      className="h-8"
                      allowClear
                    />
                  </td>
                  <td className="p-3">
                    <SearchableSelect
                      items={kindOptions}
                      value={provision.kindId}
                      onValueChange={(val) => updateProvision(index, "kindId", val)}
                      onSearchChange={handleKindSearchChange}
                      placeholder="Keine"
                      emptyText={hasKindSearch ? "Keine Ergebnisse" : "Suchen Sie nach Kunden/Leads..."}
                      className="h-8"
                      allowClear
                    />
                  </td>
                  <td className="p-3">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeProduct(index)}
                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
